import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Container,
  IconButton,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import { supabase, markingDb } from '../supabaseClient.ts';
import RichText from '../components/RichText.tsx';
import { formatMarkFraction } from '../utils/markDisplay.ts';
import type { Question } from './PracticePage/types.ts';

type ContestRow = {
  id: string;
  created_at: string;
  status: string;
  original_awarded: number;
  original_max: number;
  escalated_awarded: number | null;
  escalated_feedback: string | null;
  human_awarded: number | null;
  human_feedback: string | null;
  student_answer: string;
  question_id: string;
  questions: Pick<Question, 'id' | 'prompt' | 'marks'>;
};

const statusLabel = (status: string): string => {
  switch (status) {
    case 'human_review':
      return 'With tutor';
    case 'escalated':
      return 'AI re-checked';
    case 'open':
      return 'Open';
    case 'resolved':
      return 'Resolved';
    case 'dismissed':
      return 'Dismissed';
    default:
      return status;
  }
};

const statusColor = (
  status: string,
): 'default' | 'warning' | 'info' | 'success' | 'error' => {
  switch (status) {
    case 'human_review':
      return 'warning';
    case 'escalated':
      return 'info';
    case 'resolved':
      return 'success';
    case 'dismissed':
      return 'default';
    default:
      return 'default';
  }
};

const formatWhen = (iso: string) =>
  new Date(iso).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });

const MarkDisputesPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<ContestRow[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user) {
          setError('You must be signed in to view mark disputes.');
          return;
        }

        const { data: student } = await supabase
          .from('students')
          .select('id')
          .eq('user_id', user.id)
          .maybeSingle();

        const studentIds = Array.from(
          new Set([student?.id, user.id].filter((id): id is string => !!id)),
        );

        const { data, error: qErr } = await markingDb
          .from('mark_contests')
          .select(
            `
            id,
            created_at,
            status,
            original_awarded,
            original_max,
            escalated_awarded,
            escalated_feedback,
            human_awarded,
            human_feedback,
            student_answer,
            question_id
          `,
          )
          .in('student_id', studentIds)
          .order('created_at', { ascending: false });

        if (qErr) throw qErr;

        const contests = (data ?? []) as Omit<ContestRow, 'questions'>[];
        const questionIds = Array.from(new Set(contests.map((c) => c.question_id)));
        const questionById = new Map<string, ContestRow['questions']>();
        if (questionIds.length) {
          const { data: qRows, error: questionsErr } = await supabase
            .from('questions')
            .select('id, prompt, marks')
            .in('id', questionIds);
          if (questionsErr) throw questionsErr;
          for (const q of (qRows ?? []) as ContestRow['questions'][]) questionById.set(q.id, q);
        }

        if (!cancelled) {
          setRows(
            contests
              .filter((c) => questionById.has(c.question_id))
              .map((c) => ({ ...c, questions: questionById.get(c.question_id)! })),
          );
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to load mark disputes.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <IconButton onClick={() => navigate('/practice')} aria-label="go back" sx={{ mr: 0.5 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5">Mark disputes</Typography>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Track disputes you have sent for tutor review after re-checking with AI.
      </Typography>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && rows.length === 0 && (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="body1" sx={{ mb: 1 }}>
            No mark disputes yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            After you show an answer in practice, use &ldquo;Dispute mark&rdquo; to re-check with
            AI and optionally send the dispute to a tutor.
          </Typography>
        </Paper>
      )}

      {!loading && !error && rows.length > 0 && (
        <Stack spacing={2}>
          {rows.map((row) => {
            const finalAwarded =
              row.human_awarded ?? row.escalated_awarded ?? row.original_awarded;
            const maxMarks = row.original_max || Number(row.questions.marks ?? 0);

            return (
              <Paper key={row.id} variant="outlined" sx={{ p: 2 }}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  justifyContent="space-between"
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  spacing={1}
                  sx={{ mb: 1.5 }}
                >
                  <Typography variant="caption" color="text.secondary">
                    Submitted {formatWhen(row.created_at)}
                  </Typography>
                  <Chip
                    size="small"
                    label={statusLabel(row.status)}
                    color={statusColor(row.status)}
                    variant="outlined"
                  />
                </Stack>

                <Box sx={{ mb: 1.5 }}>
                  <RichText html={row.questions.prompt} />
                </Box>

                <Stack spacing={0.75} sx={{ mb: 1.5 }}>
                  <Typography variant="body2">
                    Original mark:{' '}
                    <strong>{formatMarkFraction(row.original_awarded, maxMarks)}</strong>
                  </Typography>
                  {row.escalated_awarded != null && (
                    <Typography variant="body2">
                      AI re-check:{' '}
                      <strong>{formatMarkFraction(row.escalated_awarded, maxMarks)}</strong>
                    </Typography>
                  )}
                  {row.human_awarded != null && (
                    <Typography variant="body2">
                      Tutor decision:{' '}
                      <strong>{formatMarkFraction(row.human_awarded, maxMarks)}</strong>
                    </Typography>
                  )}
                  {row.status !== 'resolved' &&
                    row.status !== 'dismissed' &&
                    row.human_awarded == null && (
                      <Typography variant="body2" color="text.secondary">
                        Current outcome:{' '}
                        <strong>{formatMarkFraction(finalAwarded, maxMarks)}</strong>
                      </Typography>
                    )}
                </Stack>

                {row.escalated_feedback?.trim() && (
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                      AI feedback
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {row.escalated_feedback}
                    </Typography>
                  </Box>
                )}

                {row.human_feedback?.trim() && (
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                      Tutor feedback
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {row.human_feedback}
                    </Typography>
                  </Box>
                )}

                <Typography variant="caption" color="text.secondary">
                  Question ID: {row.questions.id}
                </Typography>
              </Paper>
            );
          })}
        </Stack>
      )}
    </Container>
  );
};

export default MarkDisputesPage;
